import Actions from 'flow/actions';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import { CallDialogType } from 'flow/types/CallDialogType';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { TypedMap } from 'flow/types/TypedMap';
import { setSnackbar } from 'flow/actions/app-actions';
import { call } from 'flow/actions/phone-actions';
import { extensionStatuses } from 'flow/utils';
import { CallMethodType, isRegisteredSIP } from 'flow/utils/call-utils';
import {
    getCallMethod,
    getOwnExtension,
    getStatusOfExtension,
    isPhoneEnabled,
} from 'flow/selectors/selectors';
import { selectMakeCallsEnabled } from 'flow/selectors/app-selectors';
import { getExtension } from 'flow/selectors/ExtensionSelectors';

const openCallDialog = (
    number: string,
    extensionToCall: TypedMap<Extension> | undefined,
    extraCallOptions: object | undefined,
    dialogType: CallDialogType,
) => ({
    type: Actions.SHOW_CALL_DIALOG,
    number,
    extensionToCall,
    extraCallOptions,
    dialogType,
});

export const setCallDialogType = (dialogType: CallDialogType) => ({
    type: Actions.SET_CALL_DIALOG_TYPE,
    dialogType,
});

export const hideCallDialog = () => ({
    type: Actions.HIDE_CALL_DIALOG,
});

/**
 * Entry point for every request to call — the dialpad, a contact, a ticket, CTI and the rest.
 * Works out whether the user has to be asked anything, and either opens the dialog that asks
 * or places the call straight away.
 *
 * The decision belongs here rather than in the component that draws the dialogs, so that it
 * is taken once, against the state at the moment of the request. Deciding during render meant
 * a colleague hanging up could answer the question on the user's behalf (AC-1797).
 */
export const showCallDialog =
    (
        number: string,
        extensionToCall?: TypedMap<Extension>,
        extraCallOptions?: object,
    ) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const state = getState();

        // "Make calls" turned off. Checked here so every call the user asks for - dialpad, tel:
        // links, CTI and the rest - honours it. Calls the app places itself (co-listening, the
        // meeting rejoin fallback) go to call() directly and are not blocked.
        if (!selectMakeCallsEnabled(state)) {
            dispatch(
                setSnackbar({
                    i18nextMessage:
                        'settings.softphone.outgoing_calls_disabled',
                    hasCloseButton: true,
                }),
            );
            return;
        }

        const extensionToCallStatus = getStatusOfExtension(
            state,
            extensionToCall,
        );
        const extensionToCallIsBusy = extensionToCallStatus
            ? extensionToCallStatus !== extensionStatuses.available.key
            : false;

        if (extensionToCallIsBusy) {
            dispatch(
                openCallDialog(
                    number,
                    extensionToCall,
                    extraCallOptions,
                    CallDialogType.BUSY_EXTENSION,
                ),
            );
            return;
        }

        const callMethod = getCallMethod(state);
        const hasRegisteredTerminal = isRegisteredSIP(getOwnExtension(state));

        const mustAskForCallMethod =
            isPhoneEnabled(state) &&
            (callMethod === CallMethodType.ALWAYS_ASK ||
                (callMethod === CallMethodType.IP_PHONE &&
                    !hasRegisteredTerminal));

        if (mustAskForCallMethod) {
            dispatch(
                openCallDialog(
                    number,
                    extensionToCall,
                    extraCallOptions,
                    CallDialogType.CALL_METHOD,
                ),
            );
            return;
        }

        // A dialog left over from an earlier request would otherwise stay on screen behind
        // this call, since nothing else clears it once the call goes straight out.
        dispatch(hideCallDialog());
        dispatch(call(number, callMethod, extraCallOptions));
    };

export const showCallDialogForExtensionKey =
    (
        number: string,
        extensionKeyToCall: ExtensionKey,
        extraCallOptions?: object,
    ) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const extension = getExtension(getState(), extensionKeyToCall);
        dispatch(showCallDialog(number, extension, extraCallOptions));
    };
